import { Check, ChevronsUpDown } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/shadcn/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/shadcn/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/shadcn/popover"
import { cn } from "@/lib/utils"

interface Country {
  value: string
  label: string
}

interface CountryComboboxProps {
  countries: Country[]
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  className?: string
  disabled?: boolean
  required?: boolean
}

export function CountryCombobox({
  countries,
  value,
  onValueChange,
  placeholder = "Select country",
  searchPlaceholder = "Search country...",
  className,
  disabled = false,
}: CountryComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState("")

  // Find the country label from the ISO code
  const getCountryLabel = (code: string): string => {
    const country = countries.find(c => c && c.value === code)
    return country ? (country.label || country.value) : code
  }

  // Get the display label (country name with code)
  const selectedLabel = value ? `${getCountryLabel(value)} (${value})` : ""

  // Filter countries based on search
  const filteredCountries = React.useMemo(() => {
    if (!searchValue) return countries
    const search = searchValue.toLowerCase()
    return countries.filter(country => {
      if (!country || !country.value) return false
      const label = country.label || country.value
      return (
        label.toLowerCase().includes(search) ||
        country.value.toLowerCase().includes(search)
      )
    })
  }, [countries, searchValue])

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "bg-background hover:bg-background border-input w-full justify-between px-3 font-normal outline-offset-0 outline-none focus-visible:outline-[3px]",
            className
          )}
          disabled={disabled}
        >
          <span className={cn("truncate", !selectedLabel && "text-muted-foreground")}>
            {selectedLabel || placeholder}
          </span>
          <ChevronsUpDown className="text-muted-foreground/80 shrink-0" size={16} aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="border-input w-full min-w-[var(--radix-popper-anchor-width)] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {filteredCountries.map((country) => {
                const label = country.label || country.value
                return (
                  <CommandItem
                    key={country.value}
                    value={label}
                    onSelect={() => {
                      onValueChange?.(country.value)
                      setOpen(false)
                      setSearchValue("")
                    }}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === country.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex flex-col flex-1">
                      <span>{label}</span>
                    </div>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

